import { useCallback, useMemo } from "react";
import { useAppSelector } from ".";
import { RootState } from "@/store/store";
import { LocalStorage } from "@/constants/localStorage";
import { User } from "@/types";

const useUserProfile = (id?: string) => {
  const auth = useAppSelector((state: RootState) => state.auth);
  const users = useAppSelector((state: RootState) => state.users);

  const authUser = useMemo(() => {
    if (!auth?.user?.uid || !users[auth.user.uid]) return null;
    return users[auth.user.uid] as User;
  }, [auth?.user, users]);

  const anonymousUser = useMemo(() => {
    const lsUser = localStorage.getItem(LocalStorage.USER) ?? "{}";
    if (!lsUser) return null;
    return JSON.parse(lsUser ?? "{}") as User;
  }, []);

  const getUserById = useCallback(
    (userId?: string) => {
      if (!userId || !users[userId]) return null;
      return users[userId];
    },
    [users]
  );

  const profile = useMemo(() => {
    if (!id) return null;
    return getUserById(id);
  }, [getUserById, id]);

  const activeUser = useMemo(() => {
    if (authUser) return { ...authUser, isAnonymous: false };
    if (anonymousUser) return { ...anonymousUser, isAnonymous: true };
    return null;
  }, [authUser, anonymousUser]);

  return {
    authUser,
    anonymousUser,
    activeUser,
    getUserById,
    profile,
  };
};

export default useUserProfile;
